import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Users, DollarSign, Activity, Bot, MessageSquare,
  Loader2, RefreshCw, UserPlus, Eye, Globe,
} from 'lucide-react';
import api from '@/services/api';

import { MetricCard, HealthIndicator } from './overview/MetricCards';
import AdminOverviewCharts from './overview/AdminOverviewCharts';
import AdminOverviewDistribution from './overview/AdminOverviewDistribution';

/**
 * Admin → Overview tab. Orchestrator only; each visual chunk lives in a
 * sibling component under ./overview/. Split from a 578-line monolith in
 * iter123 to reduce React re-render surface + improve maintainability.
 */
export const AdminOverviewTab = ({ basicStats, revenueStats, smtpConfigured, onRefresh }) => {
  const [timeRange, setTimeRange] = useState('30d');
  const [detailedStats, setDetailedStats] = useState(null);
  const [programSettings, setProgramSettings] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingProgram, setSavingProgram] = useState(false);

  const loadDetailedStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/stats/detailed?time_range=${timeRange}`);
      setDetailedStats(response.data);
    } catch (error) {
      console.error('Failed to load detailed stats:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  const loadVisitorStats = useCallback(async () => {
    try {
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365;
      const response = await api.get(`/analytics/admin/dashboard-stats?days=${days}`);
      setVisitorStats(response.data);
    } catch (error) {
      console.error('Failed to load visitor stats:', error);
    }
  }, [timeRange]);

  const loadProgramSettings = useCallback(async () => {
    try {
      const response = await api.get('/admin/program-settings');
      setProgramSettings(response.data);
    } catch (error) {
      console.error('Failed to load program settings:', error);
    }
  }, []);

  useEffect(() => {
    loadDetailedStats();
    loadProgramSettings();
    loadVisitorStats();
  }, [loadDetailedStats, loadProgramSettings, loadVisitorStats]);

  const toggleProgram = async (program, enabled) => {
    setSavingProgram(true);
    try {
      await api.post('/admin/program-toggle', { program, enabled });
      setProgramSettings((prev) => ({
        ...prev,
        [program]: { ...prev[program], enabled },
      }));
      toast.success(`${program.charAt(0).toUpperCase() + program.slice(1)} program ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update program settings');
    } finally {
      setSavingProgram(false);
    }
  };

  const stats = detailedStats || {};
  const users = stats.users || {};
  const revenue = stats.revenue || {};
  const chatbots = stats.chatbots || {};
  const messages = stats.messages || {};
  const programs = stats.programs || {};
  const visitors = visitorStats?.totals || {};
  const growth = visitorStats?.growth || {};
  const trends = visitorStats?.trends || [];

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Label className="text-slate-600">Time Range:</Label>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32 bg-white border-slate-300" data-testid="overview-timerange">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => { loadDetailedStats(); loadVisitorStats(); onRefresh?.(); }}
          disabled={loading}
          className="border-slate-300 text-slate-700"
          data-testid="overview-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Visitor Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Page Views"
          value={(visitors.page_views || 0).toLocaleString()}
          icon={Eye} color="emerald"
          trend={growth.page_views ? `${growth.page_views > 0 ? '+' : ''}${growth.page_views}%` : undefined}
          subtext="Homepage visits"
        />
        <MetricCard
          label="Unique Visitors"
          value={(visitors.unique_visitors || 0).toLocaleString()}
          icon={Globe} color="blue"
          trend={growth.unique_visitors ? `${growth.unique_visitors > 0 ? '+' : ''}${growth.unique_visitors}%` : undefined}
          subtext="Distinct visitors"
        />
        <MetricCard
          label="New Leads"
          value={(visitors.leads || 0).toLocaleString()}
          icon={UserPlus} color="purple"
          trend={growth.leads ? `${growth.leads > 0 ? '+' : ''}${growth.leads}%` : undefined}
          subtext="Lead captures"
        />
        <MetricCard
          label="Messages"
          value={(visitors.messages || 0).toLocaleString()}
          icon={MessageSquare} color="amber"
          trend={growth.messages ? `${growth.messages > 0 ? '+' : ''}${growth.messages}%` : undefined}
          subtext="Chat messages"
        />
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Users"
          value={users.total || basicStats?.total_users || 0}
          icon={Users} color="blue"
          subtext={`${users.active || 0} active`}
        />
        <MetricCard
          label="Total Revenue"
          value={`$${(revenue.total || revenueStats?.total_revenue || 0).toLocaleString()}`}
          icon={DollarSign} color="green"
          subtext={`$${(revenue.in_range || 0).toLocaleString()} in period`}
        />
        <MetricCard
          label="AI Employees"
          value={chatbots.total || 0}
          icon={Bot} color="purple"
          subtext={`${chatbots.active || 0} active`}
        />
        <MetricCard
          label="Messages"
          value={(messages.total || 0).toLocaleString()}
          icon={MessageSquare} color="amber"
          subtext={`${(messages.in_range || 0).toLocaleString()} in period`}
        />
      </div>

      {/* Charts */}
      <AdminOverviewCharts users={users} revenue={revenue} trends={trends} loading={loading} />

      {/* Distribution + Programs */}
      <AdminOverviewDistribution
        users={users}
        programs={programs}
        programSettings={programSettings}
        savingProgram={savingProgram}
        onToggleProgram={toggleProgram}
      />

      {/* System Health */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-500" />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <HealthIndicator name="API Server" status="healthy" details="Response time: 45ms" />
            <HealthIndicator name="Database" status="healthy" details="MongoDB connected" />
            <HealthIndicator
              name="Email Service"
              status={smtpConfigured ? 'healthy' : 'warning'}
              details={smtpConfigured ? 'SMTP configured' : 'Not configured'}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOverviewTab;
